import { useEffect, useRef, useState } from 'react';

import { useMutation } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';

import { useDeleteOnboardingStepOptions } from '@/entities/practice-member';
import { trialSubscriptionStartedNotificationSchema } from '@/entities/subscription';
import { webSocketSubscriber } from '@/shared/api/web-socket-subscriber';

type StepStatus = 'idle' | 'in-progress' | 'completed';

const STEP_COUNT = 3;
const STEP_DURATION = 2000;
const TRANSITION_DELAY = 800;
const TRIAL_READY_TIMEOUT = 10000;

export function useDashboardSetup() {
    const { mutateAsync } = useMutation(useDeleteOnboardingStepOptions());
    const requestRef = useRef<Promise<unknown> | null>(null);
    const trialReadyRef = useRef<{ promise: Promise<void>; resolve: () => void } | null>(null);
    const failedRef = useRef(false);
    const [isError, setIsError] = useState(false);
    const [statuses, setStatuses] = useState<StepStatus[]>(
        Array.from({ length: STEP_COUNT }, (_, i) => (i === 0 ? 'in-progress' : 'idle')),
    );
    const navigate = useNavigate();

    if (!trialReadyRef.current) {
        let resolve!: () => void;
        const promise = new Promise<void>((res) => {
            resolve = res;
        });
        trialReadyRef.current = { promise, resolve };
    }

    useEffect(() => {
        const handler = () => trialReadyRef.current?.resolve();
        webSocketSubscriber.subscribe(trialSubscriptionStartedNotificationSchema, handler);
        return () =>
            webSocketSubscriber.unsubscribe(trialSubscriptionStartedNotificationSchema, handler);
    }, []);

    useEffect(() => {
        let cancelled = false;

        if (!requestRef.current) {
            const promise = mutateAsync();
            promise.catch(() => {
                failedRef.current = true;
                setIsError(true);
                setStatuses((prev) => prev.map((step) => (step === 'in-progress' ? 'idle' : step)));
            });
            requestRef.current = promise;
        }

        const runSequence = async () => {
            await new Promise((res) => setTimeout(res, TRANSITION_DELAY));

            for (let i = 0; i < STEP_COUNT; i++) {
                if (cancelled || failedRef.current) return;
                setStatuses((prev) => prev.map((s, idx) => (idx === i ? 'in-progress' : s)));
                await new Promise((res) => setTimeout(res, STEP_DURATION));

                if (i === STEP_COUNT - 1) {
                    try {
                        await requestRef.current;
                    } catch {
                        return; // mutation failure already handled by promise.catch
                    }
                    await Promise.race([
                        trialReadyRef.current?.promise,
                        new Promise((res) => setTimeout(res, TRIAL_READY_TIMEOUT)),
                    ]);
                }

                if (cancelled || failedRef.current) return;
                setStatuses((prev) => prev.map((s, idx) => (idx === i ? 'completed' : s)));
            }

            await new Promise((res) => setTimeout(res, TRANSITION_DELAY));

            if (!cancelled && !failedRef.current) {
                await navigate({ to: '/dashboard' });
            }
        };

        runSequence().catch((error) => console.error('Error in dashboard setup sequence:', error));

        return () => {
            cancelled = true;
        };
    }, [mutateAsync, navigate]);

    return { statuses, isError };
}
